import { useEffect } from 'react';
import styled from '@emotion/styled';
import { Trans, useTranslation } from 'next-i18next';

// NOTE: iOS 시스템에서의 안내를 위한 아이콘이기 때문에 디자인 시스템의 아이콘을 사용하지 않음
import IconAdd from 'src/assets/images/apple/iconAdd.svg';
import IconMore from 'src/assets/images/apple/iconMore.svg';
import IconShare from 'src/assets/images/apple/iconShare.svg';
import AzarAppIcon from 'src/assets/images/imgAzarAppIcon.svg';
import ModalLayout, { ModalContent, MobileModalType, ModalHeader } from 'src/components/ModalLayout';
import OptimizeImage from 'src/components/OptimizeImage';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType } from 'src/types/Modal';
import { DEEPLINK_FRAGMENT_PATH } from 'src/utils/deeplink';
import getDeviceInfo from 'src/utils/device/info';
import { pushEvent } from 'src/utils/event-gateway';
import { clearUrlFragment } from 'src/utils/urlFragment';
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0 40px;
  gap: 40px;
`;
const AppIcon = styled(AzarAppIcon)`
  width: 60px;
  height: 60px;
  border-radius: 16px;
`;
const LogoAndTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;
const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  width: 280px;
`;
const Title = styled.h2`
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  line-height: 40px;
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
const Description = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 400;
  color: ${({
  theme
}) => theme.color.contentDeep};
  padding: 0 16px;
`;
const StepsContainer = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 16px;
  list-style: none;
  padding: 0 16px;
  margin: 0;
  width: 100%;
  max-width: 480px;
`;
const StepItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background-color: ${({
  theme
}) => theme.color.bg2};
`;
const StepTop = styled.div`
  display: flex;
  gap: 8px;
`;
const StepNumber = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background-color: ${({
  theme
}) => theme.color.surface6};
  color: ${({
  theme
}) => theme.color.contentDeep};
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const StepTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content11};

  svg {
    display: inline-block;
    vertical-align: middle;
    margin: 0 2px;
  }
`;
const StepImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;
const StepImage = styled(OptimizeImage)`
  width: 100%;
  height: auto;
  max-width: 256px;
  object-fit: cover;
`;
const PromotionBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background-color: rgba(44, 255, 179, 0.2);
  width: 100%;
`;
const PromotionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;
const PromotionEventImage = styled.img`
  width: 80px;
  height: 80px;
`;
const PromotionEventLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  border-radius: 4px;
  background-color: ${({
  theme
}) => theme.color.surfaceBright};
  color: ${({
  theme
}) => theme.color.accentH2};
  font-size: 8px;
  font-weight: 700;
  line-height: 10px;
  width: fit-content;
`;
const PromotionTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
const PromotionDesc = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
const PromotionIconWrap = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
type BrowserType = 'safari' | 'chrome';
const getBrowserType = (): BrowserType => {
  const deviceInfo = getDeviceInfo();
  const browserName = deviceInfo.browserName?.toLowerCase() ?? '';
  if (browserName === 'chrome') return 'chrome';
  return 'safari';
};
interface StepConfig {
  stepNumber: number;
  titleKey: string;
  titleComponents?: Record<string, React.ReactElement>;
  imageSrc: string;
  imageAlt: string;
  imageWidth: number;
  imageHeight: number;
}
const SAFARI_STEPS: StepConfig[] = [{
  stepNumber: 1,
  titleKey: 'pwa_guide_step1_title',
  titleComponents: {
    font: <IconMore width={16} height={16} />
  },
  imageSrc: '/images/pwa/pwa_guide_1.png',
  imageAlt: 'pwa_guide_step1',
  imageWidth: 768,
  imageHeight: 120
}, {
  stepNumber: 2,
  titleKey: 'pwa_guide_step2_title',
  titleComponents: {
    icon: <IconShare width={16} height={16} />
  },
  imageSrc: '/images/pwa/pwa_guide_2.png',
  imageAlt: 'pwa_guide_step2',
  imageWidth: 768,
  imageHeight: 240
}, {
  stepNumber: 3,
  titleKey: 'pwa_guide_step3_title',
  titleComponents: {
    icon: <IconAdd width={16} height={16} />
  },
  imageSrc: '/images/pwa/pwa_guide_3.png',
  imageAlt: 'pwa_guide_step3',
  imageWidth: 768,
  imageHeight: 240
}, {
  stepNumber: 4,
  titleKey: 'pwa_guide_step4_title',
  imageSrc: '/images/pwa/pwa_guide_4.png',
  imageAlt: 'pwa_guide_step4',
  imageWidth: 768,
  imageHeight: 240
}];
const CHROME_STEPS: StepConfig[] = [{
  stepNumber: 1,
  titleKey: 'pwa_guide_step2_title',
  titleComponents: {
    icon: <IconShare width={16} height={16} />
  },
  imageSrc: '/images/pwa/pwa_guide_1_chrome.png',
  imageAlt: 'pwa_guide_chrome_step1',
  imageWidth: 768,
  imageHeight: 120
}, {
  stepNumber: 2,
  titleKey: 'pwa_guide_step3_title',
  titleComponents: {
    icon: <IconAdd width={16} height={16} />
  },
  imageSrc: '/images/pwa/pwa_guide_3.png',
  imageAlt: 'pwa_guide_chrome_step2',
  imageWidth: 768,
  imageHeight: 240
}, {
  stepNumber: 3,
  titleKey: 'pwa_guide_step4_title',
  imageSrc: '/images/pwa/pwa_guide_4.png',
  imageAlt: 'pwa_guide_chrome_step3',
  imageWidth: 768,
  imageHeight: 240
}];
const PwaGuideModal = () => {
  const {
    t
  } = useTranslation();
  const browserType = getBrowserType();
  const steps = browserType === 'chrome' ? CHROME_STEPS : SAFARI_STEPS;
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.POPUP,
      eventName: EVENT_NAME.PWA_GUIDE__IMPRESSION_PWA_GUIDE,
      eventParams: {
        action_category: 'impression',
        tab: 'pwa_guide',
        page: 'main',
        target: 'pwa_guide'
      }
    });
  }, []);
  useEffect(() => {
    return () => {
      clearUrlFragment(DEEPLINK_FRAGMENT_PATH.PWA_GUIDE);
    };
  }, []);
  return <ModalLayout mobileType={MobileModalType.SCREEN_MODAL} navButtonType={ModalNavButtonType.CLOSE} isDim data-sentry-element="ModalLayout" data-sentry-component="PwaGuideModal" data-sentry-source-file="index.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx" />
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <Container data-sentry-element="Container" data-sentry-source-file="index.tsx">
          <LogoAndTitleContainer data-sentry-element="LogoAndTitleContainer" data-sentry-source-file="index.tsx">
            <AppIcon data-sentry-element="AppIcon" data-sentry-source-file="index.tsx" />
            <TitleSection data-sentry-element="TitleSection" data-sentry-source-file="index.tsx">
              <Title data-sentry-element="Title" data-sentry-source-file="index.tsx">{t('pwa_guide_title')}</Title>
              <Description data-sentry-element="Description" data-sentry-source-file="index.tsx">{t('pwa_guide_desc')}</Description>
            </TitleSection>
          </LogoAndTitleContainer>
          <StepsContainer data-sentry-element="StepsContainer" data-sentry-source-file="index.tsx">
            <li>
              <PromotionBanner data-sentry-element="PromotionBanner" data-sentry-source-file="index.tsx">
                <PromotionContent data-sentry-element="PromotionContent" data-sentry-source-file="index.tsx">
                  <PromotionEventLabel data-sentry-element="PromotionEventLabel" data-sentry-source-file="index.tsx">EVENT</PromotionEventLabel>
                  <PromotionTitle data-sentry-element="PromotionTitle" data-sentry-source-file="index.tsx">{t('pwa_guide_promo_title')}</PromotionTitle>
                  <PromotionDesc data-sentry-element="PromotionDesc" data-sentry-source-file="index.tsx">{t('pwa_guide_promo_desc')}</PromotionDesc>
                </PromotionContent>
                <PromotionIconWrap data-sentry-element="PromotionIconWrap" data-sentry-source-file="index.tsx">
                  <PromotionEventImage src='/images/pwa/pwa_event.png' alt='pwa_event' data-sentry-element="PromotionEventImage" data-sentry-source-file="index.tsx" />
                </PromotionIconWrap>
              </PromotionBanner>
            </li>
            {steps.map(step => <StepItem key={step.stepNumber}>
                <StepTop>
                  <StepNumber>{step.stepNumber}</StepNumber>
                  <StepContent>
                    <StepTitle>
                      {step.titleComponents ? <Trans i18nKey={step.titleKey} components={step.titleComponents} /> : t(step.titleKey)}
                    </StepTitle>
                  </StepContent>
                </StepTop>
                <StepImageContainer>
                  <StepImage src={step.imageSrc} alt={step.imageAlt} width={step.imageWidth} height={step.imageHeight} />
                </StepImageContainer>
              </StepItem>)}
          </StepsContainer>
        </Container>
      </ModalContent>
    </ModalLayout>;
};
export default PwaGuideModal;